import type { CompanyShellRow } from "../../generated/core-api";
import { companyCellEditKey } from "../components/companyCellEditKey";
import {
  buildCompanyShellRow,
  formatCompanyShellRowForPreview,
} from "../hooks/parseCompanyShellCsv";
import type { CompanyShellCsvColumn } from "../types";

export const COMPANY_SHELL_IDENTITY_HEADERS = [
  "Company Code",
  "Company Name",
] as const;

export const COMPANY_SHELL_FIELD_GROUPS: readonly {
  id: string;
  title: string;
  headers: readonly string[];
}[] = [
  {
    id: "company",
    title: "Company",
    headers: [
      "Company IRD No.",
      "Datacom Start Date",
      "Company Group",
      "Trading Name",
      "Large Company",
      "Company Region",
      "ACR Product Type",
      "EFiling Option",
      "Delivery",
      "Delivery Note",
      "Sick Leave Stand Down",
      "FTE Standard Hours per Week",
      "Week Start Day",
      "Company Annual Leave Anniversary Date",
      "KiwiSaver Option",
      "ACC Classification Unit",
    ],
  },
  {
    id: "address",
    title: "Address",
    headers: [
      "Postal Address Line 1",
      "Postal Address Line 2",
      "Postal Address Line 3",
    ],
  },
  {
    id: "main-contact",
    title: "Main contact",
    headers: [
      "Main Contact",
      "Position (main contact)",
      "Phone (main contact)",
      "Mobile",
      "Email (main contact)",
    ],
  },
  {
    id: "secondary-contact",
    title: "Secondary contact",
    headers: [
      "Secondary Contact",
      "Position (secondary contact)",
      "Phone (secondary contact)",
      "Email (secondary contact)",
    ],
  },
  {
    id: "pay",
    title: "Pay",
    headers: [
      "First PPE Date (Weekly)",
      "Pay Group Start Date (Weekly)",
      "First PPE Date (Fortnightly)",
      "Pay Group Start Date (Fortnightly)",
      "First PPE Date (Monthly)",
      "Pay Group Start Date (Monthly)",
    ],
  },
];

export type CompanyShellDraft = Record<string, string>;

export function createCompanyShellDraft(
  row: CompanyShellRow,
  columns: readonly CompanyShellCsvColumn[]
): CompanyShellDraft {
  const display = formatCompanyShellRowForPreview(row);
  return Object.fromEntries(
    columns.map(({ header }) => [header, display[header] ?? ""])
  );
}

export function changedCompanyShellDraftHeaders(
  original: CompanyShellDraft,
  draft: CompanyShellDraft,
  columns: readonly CompanyShellCsvColumn[]
): string[] {
  return columns
    .map(({ header }) => header)
    .filter((header) => (draft[header] ?? "") !== (original[header] ?? ""));
}

interface ApplyCompanyShellDraftInput {
  rows: readonly CompanyShellRow[];
  columns: readonly CompanyShellCsvColumn[];
  rowIndex: number;
  draft: CompanyShellDraft;
  editedCellKeys: readonly string[];
  hasValidationResult: boolean;
  validationInputRevision: number;
}

export interface AppliedCompanyShellDraft {
  rows: CompanyShellRow[];
  editedCellKeys: string[];
  validateStale: boolean;
  validationInputRevision: number;
}

/**
 * Applies one whole-company draft as a single wizard-state update. The
 * existing CSV display/build mapping remains the source of truth for every
 * field conversion.
 */
export function applyCompanyShellDraft({
  rows,
  columns,
  rowIndex,
  draft,
  editedCellKeys,
  hasValidationResult,
  validationInputRevision,
}: ApplyCompanyShellDraftInput): AppliedCompanyShellDraft {
  const original = createCompanyShellDraft(rows[rowIndex], columns);
  const changedHeaders = changedCompanyShellDraftHeaders(
    original,
    draft,
    columns
  );
  const nextRows = [...rows];
  nextRows[rowIndex] = buildCompanyShellRow(
    (header) => draft[header] ?? ""
  );

  const nextEditedCellKeys = new Set(editedCellKeys);
  for (const header of changedHeaders) {
    nextEditedCellKeys.add(companyCellEditKey(rowIndex, header));
  }

  return {
    rows: nextRows,
    editedCellKeys: [...nextEditedCellKeys],
    validateStale: hasValidationResult,
    validationInputRevision: validationInputRevision + 1,
  };
}
